import type { ReactNode } from 'react';
import { PiedDePage } from './PiedDePage.tsx';
import { Link } from 'react-router';
import { IconeCheck } from '@journal/ui';

/**
 * Enveloppe des écrans hors session : connexion, inscription, parcours de
 * compte.
 *
 * ── Pourquoi deux panneaux ──────────────────────────────────────────────────
 * Un formulaire seul, centré sur un fond gris, ne dit pas ce qu'on s'apprête à
 * ouvrir — c'est l'écran de connexion générique de n'importe quel outil interne.
 * Le panneau de gauche nomme le produit et dit en trois lignes ce qu'il fait :
 * c'est la seule occasion de le faire, puisqu'une fois connecté l'utilisateur
 * ne repasse plus jamais ici.
 *
 * Il disparaît en dessous de 1024 px. Sur un téléphone, la place doit aller au
 * formulaire : un argumentaire qu'il faut faire défiler pour atteindre le champ
 * « mot de passe » agace celui qui voulait juste se connecter.
 *
 * ── Un vrai <main> par écran ────────────────────────────────────────────────
 * Ce composant fournit la structure, pas le point de repère : le formulaire
 * reste dans son propre <main>, cible du lien d'évitement et première zone
 * qu'un lecteur d'écran propose.
 */

const ARGUMENTS = [
  'Cahier journal, progressions et emploi du temps au même endroit.',
  'Le programme officiel intégré, du cycle 1 au cycle 3.',
  'Vos données hébergées en France, exportables à tout moment.',
] as const;

export function CoquilleAuth({
  titre, sousTitre, children, pied,
}: {
  titre: string;
  sousTitre?: ReactNode;
  children: ReactNode;
  /** Liens de bas de page — mot de passe oublié, création d'espace… */
  pied?: ReactNode;
}) {
  return (
    <div className="min-h-dvh bg-[var(--color-bg)] lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
      {/* ── Panneau de marque ───────────────────────────────────────────── */}
      <aside className="relative hidden overflow-hidden bg-[var(--color-primary)] p-10 text-[var(--color-primary-fg)] lg:flex lg:flex-col lg:justify-between">
        {/*
         * Deux halos très diffus. Un aplat de couleur pure sur une demi-page
         * paraît plat et bon marché ; ces dégradés lui donnent une profondeur
         * qu'on ne remarque pas consciemment. `aria-hidden` : c'est de la
         * décoration, elle n'a rien à annoncer.
         */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            backgroundImage:
              'radial-gradient(60rem 40rem at 15% -10%, rgba(255,255,255,.22), transparent 60%),'
              + 'radial-gradient(45rem 35rem at 110% 110%, rgba(0,0,0,.28), transparent 55%)',
          }}
        />

        <div className="relative flex items-center gap-3">
          <span
            aria-hidden="true"
            className="grid size-10 shrink-0 place-items-center rounded-xl bg-white/15 text-lg font-bold ring-1 ring-inset ring-white/25"
          >
            J
          </span>
          <span className="text-[15px] font-semibold tracking-tight">
            Journal de Classe
          </span>
        </div>

        <div className="relative max-w-md">
          <p className="text-[26px] font-semibold leading-tight tracking-tight">
            La préparation de classe,
            <br />
            sans la paperasse.
          </p>
          <ul className="mt-7 flex flex-col gap-3.5">
            {ARGUMENTS.map((argument) => (
              <li key={argument} className="flex items-start gap-3 text-[13.5px] leading-snug">
                <span
                  aria-hidden="true"
                  className="mt-px grid size-5 shrink-0 place-items-center rounded-full bg-white/15 ring-1 ring-inset ring-white/25"
                >
                  <IconeCheck taille={12} />
                </span>
                <span className="text-white/90">{argument}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-[11.5px] text-white/70">
          Conçu pour les enseignants du premier degré.
        </p>
      </aside>

      {/* ── Formulaire ──────────────────────────────────────────────────── */}
      <main className="flex min-h-dvh items-center justify-center px-4 py-10 sm:px-8 lg:min-h-0">
        <div className="w-full max-w-[25rem]">
          {/* Rappel de marque en tête du formulaire, uniquement là où le
              panneau de gauche est masqué — sinon le nom apparaîtrait deux
              fois côte à côte. */}
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <span
              aria-hidden="true"
              className="grid size-9 shrink-0 place-items-center rounded-xl bg-[var(--color-primary)] text-base font-bold text-[var(--color-primary-fg)]"
            >
              J
            </span>
            <span className="text-[15px] font-semibold tracking-tight">
              Journal de Classe
            </span>
          </div>

          <h1 className="text-[22px] font-semibold leading-tight tracking-tight">
            {titre}
          </h1>
          {sousTitre && (
            <p className="mt-1.5 text-[13.5px] leading-snug text-[var(--color-fg-muted)]">
              {sousTitre}
            </p>
          )}

          <div className="mt-7">{children}</div>

          {pied && (
            <div className="mt-7 flex flex-col gap-2 text-[13px] text-[var(--color-fg-muted)]">
              {pied}
            </div>
          )}

          {/* Les écrans d'inscription et de connexion sont ceux où l'on décide
              de s'engager : les conditions doivent y être atteignables sans
              chercher. */}
          <PiedDePage />
        </div>
      </main>
    </div>
  );
}

/**
 * Lien de bas de page.
 *
 * Souligné, et pas seulement coloré : le critère WCAG 1.4.1 interdit de
 * distinguer un lien de son texte environnant par la seule couleur.
 */
export function LienAuth({ vers, children }: { vers: string; children: ReactNode }) {
  return (
    <Link
      to={vers}
      className="rounded-sm font-medium text-[var(--color-primary)] underline underline-offset-2 hover:text-[var(--color-primary-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)] focus-visible:ring-offset-2"
    >
      {children}
    </Link>
  );
}
